/* global t, runtime, mergeState, safeArray, num, buildLedgerLines, dashboardGrossExpense, activePaymentsForDocument, hasPrimaryAttachment */
// LEVERANDØRGJELD — lesemodell for byråets reskontroskjerm
//
// Området har ett spørsmål saldoen ikke kan svare på alene: går kontrollkontoen
// opp mot summen av de åpne postene, og hva består de postene av. 2400 kan
// stemme til øret og likevel inneholde en faktura som er betalt to ganger, en
// betaling ingen faktura svarer for, eller en post som har ligget åpen i et år.
//
// Åpent beløp regnes PER PERIODESLUTT, ikke per i dag. En avstemming per 31.08
// skal vise gjelden slik den var 31.08, og så si at fakturaen ble betalt 03.09.
// Uten det skillet ville skjermen sammenliknet dagens reskontro med augusts
// saldo, og differansen ville vært et kunstprodukt.
//
// Byggeklossene er appens egne — `dashboardGrossExpense` for bruttobeløpet og
// `activePaymentsForDocument` for betalingene. Modellen finner ikke opp nye
// regler for hva som er betalt; den setter bare en datogrense på de samme
// postene.
//
// READ-ONLY, ingen mockdata. Har foretaket ingen åpne poster, er lista tom og
// flaten sier det.
//
// Det som IKKE er portert fra designet, fordi appen ikke har kilden:
//   · leverandørkreditnota som eget dokument — kjøpssiden har ingen kreditnota;
//     en for mye betalt faktura vises i stedet som overbetaling
//   · «betaling funnet i banken» som eget varsel — banklinjen er ikke koblet til
//     fakturaen før noen bokfører den, og et varsel bygget på lik sum og lik dag
//     ville vært en gjetning
// Å utlede dem av fritekst eller av tilfeldige beløpstreff ville vært regler
// uten kilde.
// Labels are getters so they resolve at render time and follow the UI language.
const supLabeled = (key, fallback, rest) => Object.defineProperty({ ...rest }, 'label', {
  get: () => t(key, fallback), enumerable: true,
});
const SUP_FLAG = {
  gammel: supLabeled('rcu.flag.old', 'Gammel post over 90 dager', { cls: 'error' }),
  forfalt30: supLabeled('rcu.flag.overdue30', 'Forfalt over 30 dager', { cls: 'warning' }),
  negativ: supLabeled('rcu.flag.negative', 'Negativ leverandørsaldo', { cls: 'error' }),
  duplikat: supLabeled('rcu.flag.duplicate', 'Mulig duplikat', { cls: 'error' }),
  betalt_apen: supLabeled('rcu.flag.partlyPaidOpen', 'Delbetalt, fortsatt åpen', { cls: 'warning' }),
  valuta: supLabeled('rcu.flag.currency', 'Valutakurs må kontrolleres', { cls: 'info' }),
  mangler_dok: supLabeled('rcu.flag.missingDoc', 'Mangler bilag', { cls: 'warning' }),
  betalt_etter: supLabeled('rcu.flag.paidAfter', 'Betalt etter periodedato', { cls: 'success' }),
};
// Reskontroen ligger på appens egen leverandørgjeldskonto. En annen konto i
// området har ingen underliggende reskontro, og da er differansen ikke et avvik
// men et fravær av grunnlag flaten må kunne si fra om.
const SUP_SUBLEDGER_ACCOUNT = '2400';

const SUP_AGE_BUCKETS = [
  supLabeled('rcu.age.noDue', 'Uten forfallsdato', { key: 'uten_forfall' }),
  supLabeled('rcu.age.notDue', 'Ikke forfalt', { key: 'ikke_forfalt' }),
  supLabeled('rcu.age.d1to30', '1–30 dager forfalt', { key: 'd1_30' }),
  supLabeled('rcu.age.d31to60', '31–60 dager forfalt', { key: 'd31_60' }),
  supLabeled('rcu.age.d61to90', '61–90 dager forfalt', { key: 'd61_90' }),
  supLabeled('rcu.age.over90', 'Over 90 dager forfalt', { key: 'over90' }),
];

// Alderen regnes fra FORFALLSDATOEN. En faktura med 60 dagers kreditt er ikke
// forfalt fordi den er 45 dager gammel.
const supAgeBucket = (days) => {
  if (days === null || days === undefined) return 'uten_forfall';
  if (days <= 0) return 'ikke_forfalt';
  if (days <= 30) return 'd1_30';
  if (days <= 60) return 'd31_60';
  if (days <= 90) return 'd61_90';
  return 'over90';
};

const supRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;
const supIso = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const iso = text.includes('.') ? text.split('.').reverse().join('-') : text.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
};
const supDaysBetween = (fromIso, toIso) => Math.round(
  (new Date(toIso + 'T00:00:00') - new Date(fromIso + 'T00:00:00')) / 86400000,
);

const supMonthRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    year,
    monthIndex,
    from: `${year}-${pad(monthIndex + 1)}-01`,
    to: `${year}-${pad(monthIndex + 1)}-${pad(lastDay)}`,
  };
};

// Dokumentene reskontroen består av: registrerte leverandørfakturaer. Et vanlig
// kjøp betalt på stedet går aldri om 2400 og hører ikke hjemme her.
const supIsLedgerDocument = (expense, periodTo, deferredIds) => expense
  && (expense.isSupplierInvoice || deferredIds.has(expense.id))
  && !['draft', 'deleted', 'reversed'].includes(expense.status)
  && (expense.status !== 'annulled' || (expense.fixedAssetCancellation ? !window.PlummeFixedAssetRegister.cancellationAt(expense, periodTo) : Boolean(supIso(expense.reversalDate) && supIso(expense.reversalDate) > periodTo)));

// Betalingene delt på periodedatoen: det som ble betalt før er en del av
// saldoen, det som kom etter er dokumentasjon på at posten var reell.
//
// Rekkefølgen på kildene er den samme som `dashboardPaymentSummary` bruker:
// betalingsradene først, delbetalingene på bilaget som reserve, og det gamle
// «betalt»-merket til slutt. Tre steder å avgjøre hva som er betalt ville gitt
// tre svar på hva foretaket skylder.
const supPaidAt = (state, expense, periodTo) => {
  const gross = supRound(dashboardGrossExpense(expense));
  const payments = safeArray(activePaymentsForDocument(state, 'supplier_invoice', expense.id));
  let paidBefore = 0;
  let paidAfter = 0;
  let paidAfterDate = null;
  const register = (amount, date) => {
    if (Math.abs(amount) < 0.005) return;
    if (date && date > periodTo) {
      paidAfter += amount;
      if (!paidAfterDate || date < paidAfterDate) paidAfterDate = date;
    } else paidBefore += amount;
  };
  if (payments.length) {
    for (const payment of payments) {
      const signedAmount = (payment.direction || 'outgoing') === 'incoming'
        ? -Math.max(0, num(payment.amount))
        : Math.max(0, num(payment.amount));
      register(signedAmount, supIso(payment.date) || supIso(payment.createdAt));
    }
  } else {
    const splits = safeArray(expense.paymentSplits)
      .filter((split) => split.settlementAccountCode !== SUP_SUBLEDGER_ACCOUNT && num(split.amount) > 0);
    if (splits.length) {
      // Delbetalingene på bilaget bærer ingen egen dato; de skjedde da bilaget
      // ble ført.
      for (const split of splits) register(Math.max(0, num(split.amount)), supIso(expense.date));
    } else if (expense.supplierInvoicePaidAt || (!expense.isSupplierInvoice && expense.paidAt)) {
      register(gross, supIso(expense.supplierInvoicePaidAt || expense.paidAt));
    }
  }
  for (const settlement of safeArray(state.supplierSettlements)) {
    if (settlement.status === 'reversed' || settlement.kind !== 'overpayment_offset') continue;
    if (settlement.expenseId === expense.id) register(Math.max(0, num(settlement.amount)), supIso(settlement.date));
    if (settlement.sourceExpenseId === expense.id) register(-Math.max(0, num(settlement.amount)), supIso(settlement.date));
  }
  return {
    gross,
    openAmount: supRound(gross - paidBefore),
    paidBefore: supRound(paidBefore),
    paidAfter: supRound(paidAfter),
    paidAfterDate,
  };
};

const supImportedItemKey = (item = {}) => [
  String(item.kind || '').trim(),
  String(item.invoiceNumber || '').trim().toLocaleLowerCase('nb-NO'),
  String(item.partyName || '').trim().toLocaleLowerCase('nb-NO'),
  String(item.date || '').trim(),
].join(':');

const supImportedPaidAt = (state, item, periodTo) => {
  const gross = supRound(item.remainingAmount ?? item.outstanding ?? item.amountGross ?? item.amount);
  const settlement = safeArray(state.accountingImportOpenItemSettlements)
    .find((entry) => String(entry?.itemKey || '').trim() === supImportedItemKey(item));
  const settledAmount = gross > 0 ? Math.min(gross, Math.max(0, num(settlement?.settledAmount))) : 0;
  const settledAt = supIso(settlement?.settledAt);
  const paidBefore = settledAt && settledAt <= periodTo ? settledAmount : 0;
  const paidAfter = settledAt && settledAt > periodTo ? settledAmount : 0;
  return {
    gross,
    openAmount: supRound(gross - paidBefore),
    paidBefore: supRound(paidBefore),
    paidAfter: supRound(paidAfter),
    paidAfterDate: paidAfter ? settledAt : null,
  };
};

// To fakturaer med samme leverandør, samme beløp og samme fakturadato er et
// duplikat verdt å se på. Det er et FORSLAG om gjennomgang, ikke en konklusjon:
// en leverandør kan sende to like fakturaer samme dag for to like leveranser.
const supDuplicateKey = (item) => [
  String(item.supplier || '').trim().toLowerCase(),
  item.date || '',
  Math.abs(item.gross).toFixed(2),
].join('|');

const supBuildItems = (state, periodTo) => {
  const rows = window.PlummeSupplierJournalLedger.creditItems(state, periodTo);
  const deferredIds = new Set(safeArray(state.accountingEvents).filter(event => event.source_type === 'expense' && event.accounting_date <= periodTo && safeArray(event.posting_lines).some(line => line.account === '2400' && num(line.signed_amount) < 0)).map(event => event.source_id));
  for (const expense of safeArray(state.expenses)) {
    if (!supIsLedgerDocument(expense, periodTo, deferredIds)) continue;
    const issued = supIso(expense.date);
    // En faktura mottatt etter periodeslutt er ikke en del av denne saldoen.
    if (issued && issued > periodTo) continue;
    const balance = supPaidAt(state, expense, periodTo);
    if (Math.abs(balance.openAmount) < 0.005) continue;
    const due = supIso(expense.dueDate);
    const daysOverdue = due ? supDaysBetween(due, periodTo) : null;
    const currency = String(expense.currency || 'NOK').toUpperCase();
    rows.push({
      id: expense.id,
      typeLabel: balance.openAmount < 0 ? t('rcu.type.overpayment', 'Overbetaling') : t('rcu.type.invoice', 'Faktura'),
      docNumber: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
      supplier: expense.supplierName || expense.description || t('rcu.unknownSupplier', 'Ukjent leverandør'),
      supplierId: expense.supplierId || '',
      organizationNumber: expense.supplierOrganizationNumber || '',
      date: issued,
      dueDate: due,
      daysOverdue,
      ageBucket: supAgeBucket(daysOverdue),
      gross: balance.gross,
      // Positivt beløp er det foretaket skylder. En overbetalt faktura står med
      // minus — det er leverandøren som skylder foretaket.
      openAmount: balance.openAmount,
      paidBefore: balance.paidBefore,
      paidAfter: balance.paidAfter,
      paidAfterDate: balance.paidAfterDate,
      currency,
      exchangeRate: currency === 'NOK' ? null : (num(expense.exchangeRate) || null),
      // Kontrollkontoen står på posten slik at flaten kan filtrere på den, også
      // når kontoplanen har flere kontoer i området.
      ledgerCode: SUP_SUBLEDGER_ACCOUNT,
      hasDocument: hasPrimaryAttachment(expense),
      flags: [],
      duplicateOf: [],
    });
  }

  for (const item of safeArray(state.accountingImportOpenItems?.supplierInvoices)) {
    const issued = supIso(item.date);
    if (issued && issued > periodTo) continue;
    const balance = supImportedPaidAt(state, item, periodTo);
    if (Math.abs(balance.openAmount) < 0.005) continue;
    const due = supIso(item.dueDate);
    const daysOverdue = due ? supDaysBetween(due, periodTo) : null;
    const currency = String(item.currency || 'NOK').toUpperCase();
    const importedRow = {
      id: item.id || supImportedItemKey(item),
      typeLabel: balance.openAmount < 0 ? t('rcu.type.supplierCredit', 'Leverandørkredit') : t('rcu.type.importedInvoice', 'Importert faktura'),
      docNumber: item.invoiceNumber || item.id || t('rcu.importedItem', 'Importert post'),
      supplier: item.partyName || t('rcu.unknownSupplier', 'Ukjent leverandør'),
      supplierId: item.partyId || '',
      organizationNumber: item.organizationNumber || '',
      date: issued,
      dueDate: due,
      daysOverdue,
      ageBucket: supAgeBucket(daysOverdue),
      gross: balance.gross,
      openAmount: balance.openAmount,
      paidBefore: balance.paidBefore,
      paidAfter: balance.paidAfter,
      paidAfterDate: balance.paidAfterDate,
      currency,
      exchangeRate: currency === 'NOK' ? null : (num(item.exchangeRate) || null),
      ledgerCode: SUP_SUBLEDGER_ACCOUNT,
      hasDocument: Boolean(item.importMeta?.sourcePath || item.importSource),
      imported: true,
      flags: [],
      duplicateOf: [],
    };
    const duplicateLiveRow = rows.some((row) => supDuplicateKey(row) === supDuplicateKey(importedRow)
      && String(row.docNumber || '').trim() === String(importedRow.docNumber || '').trim());
    if (!duplicateLiveRow) rows.push(importedRow);
  }

  const byDuplicateKey = new Map();
  for (const row of rows) {
    const key = supDuplicateKey(row);
    if (!byDuplicateKey.has(key)) byDuplicateKey.set(key, []);
    byDuplicateKey.get(key).push(row);
  }
  for (const group of byDuplicateKey.values()) {
    if (group.length < 2) continue;
    for (const row of group) {
      row.duplicateOf = group.filter((other) => other.id !== row.id).map((other) => other.docNumber);
    }
  }

  for (const row of rows) {
    const days = row.daysOverdue;
    if (days !== null && days > 90) row.flags.push('gammel');
    else if (days !== null && days > 30) row.flags.push('forfalt30');
    if (row.openAmount < -0.005) row.flags.push('negativ');
    if (row.duplicateOf.length) row.flags.push('duplikat');
    if (row.openAmount > 0.005 && row.paidBefore > 0.005) row.flags.push('betalt_apen');
    if (row.currency !== 'NOK') row.flags.push('valuta');
    if (!row.hasDocument) row.flags.push('mangler_dok');
    if (row.paidAfter > 0.005) row.flags.push('betalt_etter');
  }
  return rows.sort((left, right) => (right.daysOverdue ?? -9999) - (left.daysOverdue ?? -9999));
};

// Leverandøren, ikke fakturaen. Konsentrasjonen er hvor stor andel én motpart
// utgjør — et tall byrået skal se, ikke en regel appen håndhever. Grensen for
// «for mye hos én leverandør» er en faglig vurdering, ikke en terskel med kilde.
const supBuildSuppliers = (items) => {
  const byName = new Map();
  for (const item of items) {
    const key = item.supplierId ? `id:${item.supplierId}` : item.organizationNumber ? `org:${item.organizationNumber}` : `name:${String(item.supplier).trim().toLowerCase()}`;
    if (!byName.has(key)) {
      byName.set(key, {
        name: item.supplier, supplierId: item.supplierId,
        organizationNumber: item.organizationNumber,
        openBalance: 0,
        overdueBalance: 0,
        oldestDays: null,
        itemCount: 0,
        oldItemCount: 0,
        flags: new Set(),
      });
    }
    const group = byName.get(key);
    group.openBalance = supRound(group.openBalance + item.openAmount);
    group.itemCount += 1;
    if (item.daysOverdue !== null && item.daysOverdue > 0) {
      group.overdueBalance = supRound(group.overdueBalance + item.openAmount);
      if (group.oldestDays === null || item.daysOverdue > group.oldestDays) group.oldestDays = item.daysOverdue;
    }
    if (item.flags.includes('gammel')) group.oldItemCount += 1;
    for (const flag of item.flags) group.flags.add(flag);
  }
  return [...byName.values()]
    .map((group) => ({ ...group, flags: [...group.flags], negative: group.openBalance < -0.005 }))
    .sort((left, right) => Math.abs(right.openBalance) - Math.abs(left.openBalance));
};

// Kontrollkontoene i området, med reskontrosaldoen ved siden av den bokførte.
// Reskontroen står i hovedbokens fortegn: leverandørgjeld er en kreditsaldo, og
// summen av det foretaket skylder har derfor minus foran seg. Uten den snuingen
// ville «differansen» vært to ganger saldoen.
const supControlAccounts = (accounts, subledgerTotal) => safeArray(accounts).map((account) => {
  const hasSubledger = String(account.accountId) === SUP_SUBLEDGER_ACCOUNT;
  const booked = supRound(account.bookedBalance);
  return {
    accountId: account.accountId,
    accountName: account.accountName || '',
    bookedBalance: booked,
    hasSubledger,
    subledgerBalance: hasSubledger ? supRound(-subledgerTotal) : null,
    difference: hasSubledger ? supRound(booked + subledgerTotal) : null,
  };
});

// Hovedbokslinjer på kontrollkontoene som ikke kommer fra en leverandørfaktura
// eller en betaling. De har ingen reskontropost å høre til, og er derfor det
// vanligste opphavet til en differanse ingen finner.
const supDirectPostings = (state, accounts, range) => {
  const codes = safeArray(accounts).map((account) => String(account.accountId)).filter(Boolean);
  if (!codes.length) return [];
  return buildLedgerLines({ from: range.from, to: range.to, accountCodes: codes, state })
    .filter((line) => !/^(expense|payment)/i.test(String(line.sourceType || '')))
    // En debetlinje på gjeldskontoen gjør opp gjeld. Uten en faktura bak er den
    // en betaling ingen faktura svarer for; en kreditlinje er en gjeld ingen
    // faktura viser. Skillet leses av fortegnet på posteringen, ikke av teksten.
    .map((line) => ({ ...line, kind: num(line.amount) > 0 ? 'betaling' : 'gjeld' }));
};

const supIsoBefore = (iso) => {
  const date = new Date(String(iso) + 'T00:00:00');
  if (Number.isNaN(date.getTime())) return iso;
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

const buildSupplierReconciliationView = ({ periodId, accounts = [], state: suppliedState } = {}) => {
  const range = supMonthRange(periodId);
  const state = suppliedState || mergeState(runtime.appState);
  if (!range) {
    return {
      periodId, from: '', to: '', items: [], suppliers: [], aging: [], controlAccounts: [],
      directPostings: [], counts: {}, checks: [], summary: {},
    };
  }
  const items = supBuildItems(state, range.to);
  const suppliers = supBuildSuppliers(items);
  const subledgerTotal = supRound(items.reduce((sum, item) => sum + item.openAmount, 0));
  const controlAccounts = supControlAccounts(accounts, subledgerTotal);
  const directPostings = supDirectPostings(state, accounts, range);
  // Saldoen ved inngangen til perioden er de samme postene sett per dagen før.
  // Den regnes ikke ut av forrige måneds rapport: da ville to tall for det
  // samme kunne sprike.
  const openingItems = supBuildItems(state, supIsoBefore(range.from));
  const openingBalance = supRound(openingItems.reduce((sum, item) => sum + item.openAmount, 0));

  const has = (flag) => items.filter((item) => item.flags.includes(flag)).length;
  const duplicateGroups = new Set(items.filter((item) => item.duplicateOf.length).map(supDuplicateKey)).size;
  const withSubledger = controlAccounts.filter((account) => account.hasSubledger);
  const bookedSubledger = supRound(withSubledger.reduce((sum, account) => sum + account.bookedBalance, 0));
  const counts = {
    total: items.length,
    invoices: items.filter((item) => item.openAmount > 0).length,
    suppliers: suppliers.length,
    flagged: items.filter((item) => item.flags.some((flag) => flag !== 'betalt_etter')).length,
    gammel: has('gammel'),
    forfalt30: has('forfalt30'),
    negativ: has('negativ'),
    duplikat: has('duplikat'),
    duplikatGrupper: duplicateGroups,
    betaltApen: has('betalt_apen'),
    valuta: has('valuta'),
    manglerDok: has('mangler_dok'),
    betaltEtter: has('betalt_etter'),
    direktefort: directPostings.length,
    utenFaktura: directPostings.filter((line) => line.kind === 'betaling').length,
  };

  const aging = SUP_AGE_BUCKETS.map((bucket) => {
    const rows = items.filter((item) => item.ageBucket === bucket.key);
    return {
      ...bucket,
      count: rows.length,
      amount: supRound(rows.reduce((sum, row) => sum + row.openAmount, 0)),
    };
  }).filter((bucket) => bucket.count > 0);

  const checks = [
    {
      key: 'hovedbok_reskontro',
      label: t('rcu.check.ledgerVsSubledger', 'Hovedbok mot reskontro'),
      value: withSubledger.filter((account) => Math.abs(account.difference) > 0.005).length,
      note: withSubledger.length ? '' : t('rcu.check.noControlAccount', 'Ingen kontrollkonto med reskontro i området'),
    },
    { key: 'gammel', label: t('rcu.check.old', 'Gamle poster over 90 dager'), value: counts.gammel },
    { key: 'forfalt30', label: t('rcu.check.overdue30', 'Forfalte poster over 30 dager'), value: counts.forfalt30 },
    { key: 'negativ', label: t('rcu.check.negative', 'Negative leverandørsaldoer'), value: counts.negativ },
    { key: 'duplikat', label: t('rcu.check.duplicates', 'Mulige duplikater'), value: counts.duplikat },
    { key: 'uten_faktura', label: t('rcu.check.paymentsNoInvoice', 'Betalinger uten faktura'), value: counts.utenFaktura },
    { key: 'betalt_apen', label: t('rcu.check.partlyPaidOpen', 'Delbetalt, men fortsatt åpen'), value: counts.betaltApen },
    { key: 'valuta', label: t('rcu.check.currencyItems', 'Valutaposter til kontroll'), value: counts.valuta },
    { key: 'betalt_etter', label: t('rcu.flag.paidAfter', 'Betalt etter periodedato'), value: counts.betaltEtter, positive: true },
    { key: 'direktefort', label: t('rcu.check.directPostings', 'Direkteføringer på kontrollkonto'), value: counts.direktefort },
    { key: 'mangler_dok', label: t('rcu.check.missingDocs', 'Manglende bilag'), value: counts.manglerDok },
  ];

  return {
    periodId, from: range.from, to: range.to,
    items, suppliers, aging,
    controlAccounts,
    directPostings, counts, checks,
    summary: {
      subledgerTotal,
      bookedTotal: supRound(controlAccounts.reduce((sum, account) => sum + account.bookedBalance, 0)),
      subledgerBookedTotal: bookedSubledger,
      difference: supRound(bookedSubledger + subledgerTotal),
      hasSubledgerAccount: withSubledger.length > 0,
      openingBalance,
      change: supRound(subledgerTotal - openingBalance),
      overdueTotal: supRound(items.filter((item) => (item.daysOverdue || 0) > 0)
        .reduce((sum, item) => sum + item.openAmount, 0)),
      topFiveShare: (() => {
        const total = suppliers.reduce((sum, supplier) => sum + supplier.openBalance, 0);
        if (Math.abs(total) < 0.005) return null;
        return Math.round((suppliers.slice(0, 5).reduce((sum, supplier) => sum + supplier.openBalance, 0) / total) * 100);
      })(),
    },
  };
};
const buildSupplierPayablesSnapshot = ({ asOf, state: suppliedState } = {}) => supBuildSuppliers(supBuildItems(suppliedState || mergeState(runtime.appState), supIso(asOf)));
Object.assign(window, { buildSupplierReconciliationView, buildSupplierPayablesSnapshot,
  SUP_FLAG, SUP_AGE_BUCKETS, SUP_SUBLEDGER_ACCOUNT,
  supAgeBucket, supPaidAt,
});
